import { normalizeStringArray, PIN_ROLE_OPTIONS } from './compatibilityOptions';
import { formatCapabilityTagLabel, normalizeCapabilityTag } from './pinFilterGroups';
import { normalize_pin_role_value } from './pinInference';

const PRIMARY_FUNCTION_MANUAL_KEY = '__functionsManuallyEdited';
const ALTERNATE_FUNCTIONS_MANUAL_KEY = '__alternateFunctionsManuallyEdited';

const PIN_ROLE_LABELS = new Map(
  PIN_ROLE_OPTIONS
    .filter((option) => String(option?.value || '').trim() !== '')
    .map((option) => [option.value, option.label])
);

const normalizeFieldValue = (value) => String(value ?? '').trim();

const normalizeComparableRoleToken = (value) => (
  normalize_pin_role_value(value) || normalizeCapabilityTag(value)
);

export const getPinRoleDisplayLabel = (value) => {
  const normalizedRole = normalizeComparableRoleToken(value);

  if (!normalizedRole) {
    return '';
  }

  return PIN_ROLE_LABELS.get(normalizedRole) || formatCapabilityTagLabel(normalizedRole);
};

export const derivePinFunctionAutofillValues = (pin = {}) => {
  const primaryFunction = normalizeFieldValue(pin?.pin_name ?? pin?.pinName ?? '')
    || getPinRoleDisplayLabel(pin?.pin_role ?? pin?.pinRole ?? '');
  const normalizedRole = normalizeComparableRoleToken(pin?.pin_role ?? pin?.pinRole ?? '');
  const alternateLabels = [];
  const seenComparableTags = new Set();

  normalizeStringArray(pin?.capability_tags ?? pin?.capabilityTags)
    .forEach((rawTag) => {
      const normalizedTag = normalizeComparableRoleToken(rawTag);

      if (!normalizedTag || normalizedTag === normalizedRole || seenComparableTags.has(normalizedTag)) {
        return;
      }

      seenComparableTags.add(normalizedTag);

      const label = formatCapabilityTagLabel(normalizedTag);
      if (label) {
        alternateLabels.push(label);
      }
    });

  return {
    primaryFunction,
    alternateFunctions: alternateLabels.join(', '),
  };
};

const inferManualOverride = (pin, manualKey, fieldValue, suggestedValue) => {
  if (typeof pin?.[manualKey] === 'boolean') {
    return pin[manualKey];
  }

  const normalizedFieldValue = normalizeFieldValue(fieldValue);
  return normalizedFieldValue !== '' && normalizedFieldValue !== normalizeFieldValue(suggestedValue);
};

export const initializePinFunctionAutofill = (pin = {}, options = {}) => {
  const { preferSuggested = false } = options;
  const nextPin = { ...pin };
  const suggestions = derivePinFunctionAutofillValues(nextPin);
  const currentPrimaryFunction = normalizeFieldValue(nextPin.functions);
  const currentAlternateFunctions = normalizeFieldValue(nextPin.alternate_functions);
  const primaryFunctionManuallyEdited = preferSuggested
    ? false
    : inferManualOverride(nextPin, PRIMARY_FUNCTION_MANUAL_KEY, currentPrimaryFunction, suggestions.primaryFunction);
  const alternateFunctionsManuallyEdited = preferSuggested
    ? false
    : inferManualOverride(
        nextPin,
        ALTERNATE_FUNCTIONS_MANUAL_KEY,
        currentAlternateFunctions,
        suggestions.alternateFunctions
      );

  if (preferSuggested || currentPrimaryFunction === '') {
    nextPin.functions = suggestions.primaryFunction;
  }

  if (preferSuggested || currentAlternateFunctions === '') {
    nextPin.alternate_functions = suggestions.alternateFunctions;
  }

  nextPin[PRIMARY_FUNCTION_MANUAL_KEY] = primaryFunctionManuallyEdited;
  nextPin[ALTERNATE_FUNCTIONS_MANUAL_KEY] = alternateFunctionsManuallyEdited;

  return nextPin;
};

export const updatePinWithFunctionAutofill = (pin = {}, field, value) => {
  const nextPin = {
    ...pin,
    [field]: value,
  };
  const nextSuggestions = derivePinFunctionAutofillValues(nextPin);
  const isSuggestionSourceField = (
    field === 'pin_name'
    || field === 'pinName'
    || field === 'pin_role'
    || field === 'pinRole'
    || field === 'capability_tags'
    || field === 'capabilityTags'
  );

  if (field === 'functions') {
    nextPin[PRIMARY_FUNCTION_MANUAL_KEY] = (
      normalizeFieldValue(value) !== normalizeFieldValue(nextSuggestions.primaryFunction)
    );
    return nextPin;
  }

  if (field === 'alternate_functions') {
    nextPin[ALTERNATE_FUNCTIONS_MANUAL_KEY] = (
      normalizeFieldValue(value) !== normalizeFieldValue(nextSuggestions.alternateFunctions)
    );
    return nextPin;
  }

  const currentSuggestions = derivePinFunctionAutofillValues(pin);
  let primaryFunctionManuallyEdited = inferManualOverride(
    pin,
    PRIMARY_FUNCTION_MANUAL_KEY,
    pin?.functions,
    currentSuggestions.primaryFunction
  );
  let alternateFunctionsManuallyEdited = inferManualOverride(
    pin,
    ALTERNATE_FUNCTIONS_MANUAL_KEY,
    pin?.alternate_functions,
    currentSuggestions.alternateFunctions
  );

  if (isSuggestionSourceField) {
    if (normalizeFieldValue(nextPin.functions) === normalizeFieldValue(nextSuggestions.primaryFunction)) {
      primaryFunctionManuallyEdited = false;
    }

    if (
      normalizeFieldValue(nextPin.alternate_functions)
      === normalizeFieldValue(nextSuggestions.alternateFunctions)
    ) {
      alternateFunctionsManuallyEdited = false;
    }

    if (!primaryFunctionManuallyEdited) {
      nextPin.functions = nextSuggestions.primaryFunction;
    }

    if (!alternateFunctionsManuallyEdited) {
      nextPin.alternate_functions = nextSuggestions.alternateFunctions;
    }
  }

  nextPin[PRIMARY_FUNCTION_MANUAL_KEY] = primaryFunctionManuallyEdited;
  nextPin[ALTERNATE_FUNCTIONS_MANUAL_KEY] = alternateFunctionsManuallyEdited;

  return nextPin;
};
